/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
// Convert an array to an object.
export function arrayToObj(arr, prefix = '') {
    const obj = {};
    let index = 0;
    for (const item of arr) {
        obj[prefix + (index++).toString()] = item;
    }
    return obj;
}
// Create a deep copy of supplied argument.
export function deepCopy(orig) {
    if (orig === null)
        return orig;
    if (orig instanceof Date)
        return new Date(orig.getTime());
    if (orig instanceof Array) {
        const copy = [];
        for (const value of orig) {
            copy.push(deepCopy(value));
        }
        return copy;
    }
    if (typeof orig === 'object') {
        const copy = {};
        for (const key of Object.keys(orig)) {
            copy[key] = deepCopy(orig[key]);
        }
        return copy;
    }
    return orig;
}
// Filter an array in place. (Array.filter creates a new array).
//
// Reference: https://stackoverflow.com/a/37319954/122231
export function filterInPlace(arr, test) {
    let i = 0;
    let j = 0;
    while (i < arr.length) {
        const val = arr[i];
        if (test(val, i))
            arr[j++] = val;
        i++;
    }
    arr.length = j;
    return arr;
}
// Filter an object, returning a new object with items passing the test.
export function filterObject(obj, test) {
    const newObject = {};
    for (const [id, value] of Object.entries(obj)) {
        if (test(id, value))
            newObject[id] = value;
    }
    return newObject;
}
// Filter an object in place.
export function filterObjectInPlace(obj, test) {
    for (const [id, value] of Object.entries(obj)) {
        if (!test(id, value))
            delete obj[id];
    }
    return obj;
}
// Returns an object showing the changes between the two objects. If a property was removed, its
// value will be null in the returned object.
export function getChanges(prev, curr) {
    const changed = {};
    const keys = new Set([...Object.keys(prev), ...Object.keys(curr)]);
    for (const key of keys) {
        if (prev[key] === undefined && curr[key] !== undefined) {
            changed[key] = curr[key];
        }
        else if (prev[key] !== undefined && curr[key] === undefined) {
            changed[key] = null;
        }
        else if (prev[key] !== undefined && curr[key] !== undefined) {
            if (typeof prev[key] !== typeof curr[key]) {
                changed[key] = curr[key];
            }
            else if (typeof prev[key] === 'object') {
                if (prev[key] === null && curr[key] === null) {
                    // nop
                }
                else if (prev[key] === null || curr[key] === null) {
                    changed[key] = curr[key];
                }
                else {
                    const child = getChanges(prev[key], curr[key]);
                    if (child !== undefined)
                        changed[key] = child;
                }
            }
            else if (prev[key] !== curr[key]) {
                changed[key] = curr[key];
            }
        }
    }
    if (Object.keys(changed).length === 0)
        return undefined;
    return changed;
}
// Get the value at the nested property of the specified object. Returns undefined if the property
// doesn't exist. See setProp for how to specify the path.
//
// Note: This method should be used sparingly in new Typescript code. In Typescript, this can be
// accomplished by using optional chaning, and a default can be applied with nullish coalescing.
//
// const x = foo?.[bar]?.baz ?? 'default';
//
// However, this method can still be useful when programmatically specifying complex paths.
export function getProp(obj, path, defaultValue) {
    if (obj === undefined || obj === null)
        return defaultValue;
    if (typeof path === 'string')
        path = path.split('.');
    for (const prop of path) {
        if (obj === undefined ||
            obj === null ||
            (typeof obj !== 'object' && typeof obj !== 'function') ||
            !(prop in obj)) {
            return defaultValue;
        }
        obj = obj[prop];
        if (obj === undefined || obj === null)
            return undefined;
    }
    return obj;
}
// Like getProp, but path components may be wildcards, "*".
//
// The return value is an array of tuples containing the matched paths and values at that path.
//
// Example:
//
// const pockets = {
//     frontLeft: {
//         contents: {
//             a0: 'car key',
//             a1: 'lint',
//         },
//         other: {},
//     },
//     frontRight: {
//         contents: {
//             a0: 'phone',
//         },
//         other: {},
//     },
// };
//
// const contents = getPropsWithWildcards(pockets, ['*', 'contents', '*']);
//
// contents will be: [
//     [['frontLeft', 'contents', 'a0'], 'car key'],
//     [['frontLeft', 'contents', 'a1'], 'lint'],
//     [['frontRight', 'contents', 'a0'], 'phone'],
// ];
//
// const other = getPropsWithWildcards(pockets, ['*', 'other', '*']);
//
// other will be: []
//
// const nothing = getPropsWithWildcards(pockets, ['*', 'nothing', '*']);
//
// nothing will be: []
export function getPropsWithWildcards(obj, path, workingPath = []) {
    if (path.length === 0) {
        return obj === undefined || obj === null ? [] : [[workingPath, obj]];
    }
    else {
        if (obj === undefined || obj === null || typeof obj !== 'object')
            return [];
        const pathElement = path[0];
        const props = pathElement === '*' ? Object.keys(obj) : [pathElement];
        const result = [];
        for (const prop of props) {
            result.push(...getPropsWithWildcards(obj[prop], path.slice(1), [...workingPath, prop]));
        }
        return result;
    }
}
// Perform a merge of object b into object a (object a is modified).
export function merge(a, b) {
    if (a === undefined || a === null || typeof a !== 'object')
        return b;
    if (b === undefined || b === null || typeof b !== 'object')
        return b;
    const aa = a;
    const bb = b;
    for (const prop of Object.keys(bb)) {
        if (typeof aa[prop] === 'object') {
            if (typeof bb[prop] === 'object') {
                merge(aa[prop], bb[prop]);
            }
            else {
                aa[prop] = bb[prop];
            }
        }
        else {
            aa[prop] = bb[prop];
        }
    }
    return aa;
}
// Return true if the two objects are equal, false if not.
export function objEquals(a, b) {
    if (a === undefined && b === undefined)
        return true;
    if (a === null && b === null)
        return true;
    if (a === undefined || b === undefined || a === null || b === null)
        return false;
    if (Array.isArray(a) && Array.isArray(b)) {
        if (a.length !== b.length)
            return false;
        for (let i = 0; i < a.length; i++) {
            if (!objEquals(a[i], b[i]))
                return false;
        }
        return true;
    }
    if (typeof a !== 'object' || typeof b !== 'object')
        return a === b;
    const aKeys = Object.keys(a);
    const bKeys = Object.keys(b);
    if (aKeys.length !== bKeys.length)
        return false;
    for (const aKey in a) {
        if (!(aKey in a))
            continue;
        if (!(aKey in b))
            return false;
        if (typeof a[aKey] !== typeof b[aKey])
            return false;
        if (typeof a[aKey] === 'object') {
            if (!objEquals(a[aKey], b[aKey]))
                return false;
        }
        else {
            if (a[aKey] !== b[aKey])
                return false;
        }
    }
    return true;
}
export function objToArray(obj, prefix = '') {
    return Object.entries(obj)
        .map(e => [parseInt(e[0].substring(prefix.length), 10), e[1]])
        .sort((a, b) => a[0] - b[0])
        .map(e => e[1]);
}
// Traverses all properties in the object and deletes any that are undefined.
//
// const obj = { a: 1, b: { c: undefined, d: null }, e: { f: undefined, g: 2 } };
// pruneEmptyObjects(obj);
//
// --> obj = { a: 1, e: { g: 2 } }
//
// The object is returned from the function, but using the return value is usually not necessary
// because the operations are done in place. One case where the return value is useful is when the
// resulting object would be empty and you want your new value to be undefined.
//
// const obj = { a: undefined };
// const returnedObj = pruneEmptyObjects(obj);
//
// --> obj = {}
// --> returnedObj = undefined
export function pruneEmptyObjects(obj) {
    if (obj === undefined || typeof obj !== 'object' || obj === null)
        return;
    for (const [key, item] of Object.entries(obj)) {
        if (item === undefined) {
            delete obj[key];
        }
        else if (typeof item === 'object' && item !== null) {
            pruneEmptyObjects(item);
            if (Object.keys(item).length === 0)
                delete obj[key];
        }
    }
    return Object.keys(obj).length === 0 ? undefined : obj;
}
export function push(arr, value) {
    if (Array.isArray(value)) {
        arr.push(...value);
    }
    else {
        arr.push(value);
    }
    return arr;
}
// Set value at a nested property in an object. Create parent nodes if they don't exist. Nested
// properties are specified with the path argument in two ways.
//
// 1. A string with the properties separated by dots, e.g., `foo.${bar}.baz`.
// 2. An array, e.g., ['foo', bar, 'baz].
//
// The old setNode method is deprecated in favor of setProp which performs the same function, but
// uses a more descriptive name and supports optional generic typing.
//
// Note: unlike the other nested property utility methods, setProp is NOT deprecated for new
// Typescript code because Typescript's optional chaining does not support optional property
// assignment. (https://github.com/tc39/proposal-optional-chaining). Still, use of setProp is
// discouraged because it defeats type checking.
export function setProp(obj, path, value) {
    if (obj === undefined || obj === null)
        return obj;
    if (typeof path === 'string')
        path = path.split('.');
    const orig = obj;
    for (let i = 0; i < path.length - 1; i++) {
        if (!(path[i] in obj)) {
            obj[path[i]] = {};
        }
        obj = obj[path[i]];
    }
    obj[path[path.length - 1]] = value;
    return orig;
}
export function deleteProp(obj, path) {
    if (obj === undefined || obj === null)
        return;
    if (typeof path === 'string')
        path = path.split('.');
    deletePropInternal(obj, [...path]);
}
function deletePropInternal(obj, path) {
    if (obj === undefined || obj === null)
        return;
    if (path.length <= 0) {
        return; // error: shouldn't happen
    }
    else if (path.length === 1) {
        delete obj[path[0]];
    }
    else {
        const prop = path.shift();
        if (prop !== undefined) {
            deletePropInternal(obj[prop], path);
            if (Object.keys(obj[prop]).length === 0) {
                delete obj[prop];
            }
        }
    }
}
